import { useState } from "react";
import type { TypeConfig } from "../../shared/types";
import { api } from "../lib/api";
import Modal from "./Modal";
import { useConfirm } from "./ConfirmProvider";

const SWATCHES = [
  "#2E90FA", "#7F56D9", "#F79009", "#F04438", "#17B886",
  "#12B76A", "#667085", "#EE46BC", "#0BA5EC", "#EF6820",
];

const CORE_KEYS = ["fixed", "variable", "debt", "plan"];

// Add a new type, or edit an existing one's label, description and colour.
// Custom (non built-in) types can also be deleted here.
export default function TypeModal({
  type,
  onClose,
  onSaved,
}: {
  type: TypeConfig | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = type != null;
  const canDelete = editing && !CORE_KEYS.includes(type.key);
  const [label, setLabel] = useState(type?.label ?? "");
  const [description, setDescription] = useState(type?.description ?? "");
  const [colour, setColour] = useState(type?.colour ?? SWATCHES[0]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!label.trim()) return setError("Give the type a label");
    setSaving(true);
    try {
      if (editing) await api.updateType(type.key, label.trim(), description.trim(), colour);
      else await api.createType(label.trim(), description.trim(), colour);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  const ask = useConfirm();

  async function remove() {
    if (!type) return;
    if (!(await ask({ title: "Delete type?", message: `Delete the "${type.label}" type?`, confirmLabel: "Delete", danger: true }))) return;
    setSaving(true);
    try {
      await api.deleteType(type.key);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={editing ? `Edit “${type.label}”` : "Add type"} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span className="field__label">Label</span>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Subscriptions" autoFocus />
        </label>
        <label className="field">
          <span className="field__label">Description</span>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Set monthly costs" />
        </label>
        <div className="field">
          <span className="field__label">Colour</span>
          <div className="swatches">
            {SWATCHES.map((c) => (
              <button key={c} type="button" className={"swatch" + (colour.toLowerCase() === c.toLowerCase() ? " is-active" : "")} style={{ background: c }} onClick={() => setColour(c)} aria-label={c} />
            ))}
            <input type="color" className="swatch swatch--custom" value={colour} onChange={(e) => setColour(e.target.value)} aria-label="Custom colour" />
          </div>
        </div>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions form__actions--split">
          {canDelete ? (
            <button className="btn btn--danger" onClick={remove} disabled={saving}>Delete</button>
          ) : <span />}
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : editing ? "Save" : "Add type"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
